import { useMemo, useState } from "react";
import { useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router";
import { Button } from "@/components/design/Button";
import { Avatar } from "@/components/common/Avatar";
import { personDisplayName, personDisplayRole } from "@/lib/person-display";
import { Select } from "@/components/design/Select";
import { Disclosure } from "@/components/design/Disclosure";
import { Switch } from "@/components/design/Switch";
import { Tooltip } from "@/components/common/Tooltip";
import { MatchTeamProfilesDocument, SearchPersonsDocument } from "@/graphql/generated/operations";
import { PowerProfileEditor } from "./PowerProfileEditor";
import { userErrorMessage } from "@/lib/user-error";
import { TeamMemberPicker } from "./TeamMemberPicker";
import { densifyElementWeights, normalizePowersWeight, POWERS_WEIGHT_MIN } from "./match-design-rules";
import type { RecruitingTeamFitBasis } from "./design-codec";
import type { MatchData } from "./match-design-types";
import type { ResultPerson } from "./match-result-derive";

export function TeamContextPanel({ data, update, readOnly = false }: {
  data: MatchData;
  update: (patch: Partial<MatchData>) => void;
  readOnly?: boolean;
}) {
  const intl = useIntl();
  const setup = data.recruitingTeamFitSetup;
  const [enabled, setEnabled] = useState(setup?.enabled ?? Boolean(data.recruitingTeamFit));
  const [open, setOpen] = useState(enabled);
  const [members, setMembers] = useState<readonly ResultPerson[]>(setup?.members ?? data.recruitingTeamFit?.members ?? []);
  const [scorecard, setScorecard] = useState(setup?.scorecard ?? data.recruitingTeamFit?.scorecard ?? null);
  const controlsDisabled = readOnly || !enabled;
  const [pickerOpen, setPickerOpen] = useState(false);
  const query = useQuery(MatchTeamProfilesDocument, {
    variables: { input: { first: 50 } }, skip: readOnly,
  });
  const profiles = useMemo(() => (query.data?.teamProfiles?.nodes ?? []).map(profile => ({
    id: profile.id,
    name: profile.localizedName?.localized || profile.name || profile.id,
    elementWeights: densifyElementWeights(Object.fromEntries((profile.weights ?? []).flatMap(weight =>
      weight?.element ? [[weight.element, normalizePowersWeight(weight.weight)]] : [])), POWERS_WEIGHT_MIN),
  })), [query.data]);
  const memberIds = useMemo(() => members.map(member => member.id), [members]);
  const hydration = useQuery(SearchPersonsDocument, {
    variables: { input: { where: { id: { in: memberIds } }, first: memberIds.length } },
    skip: memberIds.length === 0,
  });
  const hydratedById = useMemo(() => new Map((hydration.data?.persons?.nodes ?? [])
    .map(node => [node.id, node])), [hydration.data]);

  const sourcePreset = profiles.find(profile => profile.id === scorecard?.id);
  const modified = !!scorecard && !!sourcePreset && Object.keys(sourcePreset.elementWeights).some(element =>
    (scorecard.elementWeights[element] ?? 0) !== sourcePreset.elementWeights[element]);
  const options = profiles.map(profile => ({ value: profile.id, label: profile.name +
    (modified && scorecard?.id === profile.id ? intl.formatMessage({ id: "match.teamProfileModifiedSuffix" }) : "") }));
  if (scorecard && !options.some(option => option.value === scorecard.id)) {
    options.unshift({ value: scorecard.id, label: scorecard.name });
  }

  const missing = !enabled ? null : members.length === 0 ? "members" : !scorecard ? "scorecard"
    : Object.values(scorecard.elementWeights).some(weight => weight > 0) ? null : "weights";

  function change(nextMembers: readonly ResultPerson[], nextScorecard: RecruitingTeamFitBasis["scorecard"] | null, nextEnabled = enabled) {
    setMembers(nextMembers);
    setScorecard(nextScorecard);
    const storedMembers = nextMembers.map(({ id, name }) => ({ id, name }));
    const basis = nextEnabled && nextMembers.length > 0 && nextScorecard && Object.values(nextScorecard.elementWeights).some(weight => weight > 0)
      ? { members: storedMembers, scorecard: nextScorecard }
      : null;
    update({ recruitingTeamFit: basis, recruitingTeamFitSetup: {
      enabled: nextEnabled, members: storedMembers, scorecard: nextScorecard,
    } });
  }

  return <Disclosure id="team-context-disclosure" open={open} onToggle={() => setOpen(!open)}
    title={<FormattedMessage id="match.teamContext.title" defaultMessage="Team-Fit" />}
    count={!open && missing ? intl.formatMessage({ id: "match.teamContext.incomplete" }) : null}
    headerAction={<Tooltip placement="left" content={<FormattedMessage id="match.teamContext.toggleHint" defaultMessage="Every candidate also gets a team contribution: how much of what this team is still missing they cover. Measured against the team Power Scorecard, where powers the team is thin on count for more. Calculated automatically for every candidate." />}>
      <Switch id="team-context-toggle" checked={enabled} disabled={readOnly}
        ariaLabel={intl.formatMessage({ id: "match.teamContext.enable", defaultMessage: "Use team context" })}
        onChange={next => {
          setEnabled(next);
          setOpen(next);
          setPickerOpen(false);
          change(members, scorecard, next);
        }} />
    </Tooltip>}>
    <div className={`space-y-4 transition ${enabled ? "" : "opacity-60 blur-[2px]"}`} inert={!enabled}>
      <div>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <span className="smycles-field-label"><FormattedMessage id="match.teamFit.pickerTitle" defaultMessage="Current team" /></span>
          {!readOnly ? <Button id="team-context-choose-members" variant="secondary" size="sm" disabled={controlsDisabled} aria-describedby={missing === "members" ? "team-context-missing-members" : undefined} onClick={() => setPickerOpen(true)}>
            <FormattedMessage id="match.teamContext.chooseMembers" defaultMessage="Choose people" />
          </Button> : null}
        </div>
        {/* Die Aufgabe hier ist Bestaetigen, nicht Auswaehlen: erkennen, wer drin ist, und einen
            Falschen wieder rauswerfen. Gesicht und Name leisten das, die Rolle nicht mehr - die
            gehoert in den Picker, wo zwischen drei Annas entschieden wird. Als volle Zeilen kostete
            diese Bestaetigung das Sechsfache an Hoehe; ein Gesicht kostet Breite, davon ist hier
            genug da. Die Rolle bleibt erreichbar: am title und ein Profil weiter.
            Avatar und Rolle stehen NICHT im Design (dort nur id + name, damit der Snapshot klein
            bleibt), werden also live zu den gespeicherten Ids geholt. */}
        {members.length > 0 ? <ul className="flex flex-wrap gap-2">{members.map(member => {
          const hydrated = hydratedById.get(member.id);
          const name = hydrated ? personDisplayName(hydrated) : member.name;
          const role = hydrated ? personDisplayRole(hydrated) : null;
          return <li key={member.id}
            className="flex min-h-10 max-w-full items-center gap-2 rounded-full bg-ink/[0.06] py-1 pl-1 pr-1">
            <Avatar size="sm" person={{ name,
              avatarContent: hydrated?.avatar?.content ?? null,
              avatarMime: hydrated?.avatar?.mimeType ?? null }} />
            <Link
              id={`team-context-member-${member.id}`}
              to={`/p/${member.id}`}
              title={role ?? undefined}
              className="truncate rounded-sm text-smycles-meta text-ink hover:underline focus:outline-none focus-visible:ring-1 focus-visible:ring-ink/30"
            >
              {name}
            </Link>
            {!readOnly ? <Button id={`team-context-remove-${member.id}`} variant="icon" size="sm" disabled={controlsDisabled}
              aria-label={intl.formatMessage({ id: "match.teamFit.remove", defaultMessage: "Remove {name}" }, { name })}
              onClick={() => change(members.filter(person => person.id !== member.id), scorecard)}>
              <span className="block -translate-y-px text-smycles-title leading-none">×</span>
            </Button> : null}
          </li>;
        })}</ul> : null}
        {missing === "members" ? <p className="smycles-field-meta mt-2 max-w-xl text-ink-muted" role="status" id="team-context-missing-members">
          <FormattedMessage id="match.teamContext.missingMembers" defaultMessage="Team-Fit is not calculated without an existing team." />
        </p> : null}
      </div>
      <div>
        <label className="block space-y-2">
          <span className="smycles-field-label"><FormattedMessage id="match.teamFit.scorecardLabel" defaultMessage="Team Power Scorecard" /></span>
          {readOnly ? <p className="smycles-field-value">{scorecard?.name ?? "–"}</p> : <Select id="team-context-scorecard" value={scorecard?.id ?? ""} options={options}
            placeholder={intl.formatMessage({ id: "match.teamFit.scorecardPlaceholder", defaultMessage: "Select" })}
            disabled={query.loading || controlsDisabled}
            onChange={id => change(members, profiles.find(profile => profile.id === id) ?? scorecard)} />}
        </label>
        {missing === "scorecard" ? <p className="smycles-field-meta mt-2 max-w-xl text-ink-muted" role="status" id="team-context-missing-scorecard">
          <FormattedMessage id="match.teamContext.missingScorecard" defaultMessage="Team-Fit is not calculated without a Power Scorecard." />
        </p> : null}
      </div>
      {query.error ? <p className="text-smycles-label text-red-600">
        <FormattedMessage id="match.profilesLoadError" values={{ message: userErrorMessage(intl, query.error) }} />
      </p> : null}
      {scorecard ? <PowerProfileEditor profile={scorecard} onChange={next => change(members, next)}
        readOnly={controlsDisabled} idPrefix="team-context-powerprofile" /> : null}
      {missing === "weights" ? <p className="smycles-field-meta max-w-xl text-ink-muted" role="status" id="team-context-missing-weights">
        <FormattedMessage id="match.teamContext.missingWeights" defaultMessage="Give at least one power a weight to calculate Team-Fit." />
      </p> : null}
      {!readOnly && pickerOpen && <TeamMemberPicker open={pickerOpen} onClose={() => setPickerOpen(false)}
        chosenIds={members.map(member => member.id)} onPick={person => {
          if (!members.some(member => member.id === person.id)) change([...members, person], scorecard);
        }} />}
    </div>
  </Disclosure>;
}
